import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, GraduationCap, Search, Loader2, Upload, CheckCircle2, FileText, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, API, DOC_META, STATUS_META, KELAS_LIST, fileUrl } from "@/lib/api";

const empty = {
  nama_lengkap: "", nama_panggilan: "", nisn: "", nis: "", nik: "",
  jenis_kelamin: "", tempat_lahir: "", tanggal_lahir: "", agama: "",
  kewarganegaraan: "Indonesia", anak_ke: "", jumlah_saudara: "",
  jumlah_saudara_tiri: "", jumlah_saudara_angkat: "", anak_yatim: "Tidak",
  bahasa: "Bahasa Indonesia", golongan_darah: "",
  penyakit: "", kelainan_jasmani: "",
  tinggi_badan: "", berat_badan: "",
  alamat: "", kelurahan: "", kecamatan: "", kabupaten: "", provinsi: "", kode_pos: "",
  nomor_hp: "", tinggal_bersama: "", jarak_sekolah: "",
  asal_sekolah: "", npsn_asal: "", nomor_ijazah: "", tahun_ijazah: "", nomor_sttb: "",
  lama_belajar: "", nomor_skhun: "",
  kelas: "", tanggal_diterima: "", ditingkat: "",
  pindahan_dari: "", pindahan_alasan: "",
  kegemaran_kesenian: "", kegemaran_olahraga: "", kegemaran_organisasi: "", kegemaran_lain: "",
  ayah: { nama: "", nik: "", tempat_lahir: "", tanggal_lahir: "", agama: "", pendidikan: "", pekerjaan: "", penghasilan: "", nomor_hp: "", alamat: "" },
  ibu: { nama: "", nik: "", tempat_lahir: "", tanggal_lahir: "", agama: "", pendidikan: "", pekerjaan: "", penghasilan: "", nomor_hp: "", alamat: "" },
  wali: { nama: "", nik: "", hubungan: "", pekerjaan: "", penghasilan: "", nomor_hp: "", alamat: "" },
};

export default function StudentPortal() {
  const nav = useNavigate();
  const [step, setStep] = useState("search"); // search | form
  const [nisnLookup, setNisnLookup] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(empty);
  const [tab, setTab] = useState("pribadi");

  const startNew = async () => {
    if (!nisnLookup || nisnLookup.length < 4) {
      toast.error("NISN minimal 4 digit");
      return;
    }
    setLoading(true);
    try {
      const { data: existing } = await api.get(`/public/students/${nisnLookup}`);
      setData({ ...empty, ...existing, ayah: existing.ayah || empty.ayah, ibu: existing.ibu || empty.ibu, wali: existing.wali || empty.wali });
      toast.info("Data ditemukan, silakan lanjutkan pengisian");
      setStep("form");
    } catch (err) {
      if (err.response?.status === 404) {
        setData({ ...empty, nisn: nisnLookup });
        setStep("form");
      } else {
        toast.error("Gagal memuat data");
      }
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setNested = (parent, k, v) => setData((d) => ({ ...d, [parent]: { ...(d[parent] || {}), [k]: v } }));

  const save = async () => {
    if (!data.nama_lengkap || !data.nisn) {
      toast.error("Nama Lengkap dan NISN wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...data };
      const saved = await api.post("/public/students", payload);
      setData({ ...empty, ...saved.data, ayah: saved.data.ayah || empty.ayah, ibu: saved.data.ibu || empty.ibu, wali: saved.data.wali || empty.wali });
      toast.success("Biodata berhasil disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setLoading(false);
    }
  };

  const uploadDoc = async (docType, file) => {
    if (!data.nisn) {
      toast.error("Isi NISN dan simpan biodata dulu");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/public/students/${data.nisn}/documents/${docType}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${DOC_META[docType].label} berhasil diunggah`);
      const { data: refreshed } = await api.get(`/public/students/${data.nisn}`);
      setData((d) => ({ ...d, documents: refreshed.documents, status: refreshed.status }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah");
    }
  };

  if (step === "search") {
    return (
      <div className="min-h-screen bg-slate-50/70 bg-grid" data-testid="student-portal-search">
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900 text-sm">
            <ArrowLeft className="w-4 h-4" /> Kembali
          </Link>
          <div className="flex items-center gap-2">
            <img src="/logo-sekolah.png" alt="Logo" className="w-8 h-8 rounded object-contain bg-white border border-slate-200 p-0.5" />
            <div className="text-sm">
              <div className="font-display font-bold text-slate-900 leading-none">Portal Siswa</div>
              <div className="text-[10px] text-slate-500">SMPN 1 Penengahan</div>
            </div>
          </div>
          <div className="w-16" />
        </div>
      </header>
        <div className="max-w-xl mx-auto px-6 py-16">
          <Card className="border-slate-200 shadow-lg rounded-2xl">
            <CardHeader className="text-center pb-4">
              <div className="w-14 h-14 rounded-xl bg-blue-700 grid place-items-center mx-auto mb-3">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-2xl font-display font-bold">Selamat Datang, Siswa</CardTitle>
              <CardDescription>Masukkan NISN untuk mulai mengisi Buku Induk atau melanjutkan data yang tersimpan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="nisn-lookup" className="text-slate-700">Nomor Induk Siswa Nasional (NISN)</Label>
                  <Input
                    id="nisn-lookup"
                    value={nisnLookup}
                    onChange={(e) => setNisnLookup(e.target.value.replace(/\D/g, ""))}
                    placeholder="Contoh: 0123456789"
                    className="mt-1.5 h-11 font-mono"
                    maxLength={10}
                    data-testid="nisn-lookup-input"
                  />
                </div>
                <Button onClick={startNew} disabled={loading} className="w-full h-11 bg-blue-700 hover:bg-blue-800 rounded-lg" data-testid="start-form-button">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Lanjutkan
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statusMeta = STATUS_META[data.status] || STATUS_META.draft;

  return (
    <div className="min-h-screen bg-slate-50/70" data-testid="student-portal-form">
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <button onClick={() => setStep("search")} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-xs sm:text-sm shrink-0" data-testid="back-to-search-button">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Ganti NISN</span>
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
            <img src="/logo-sekolah.png" alt="Logo" className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 text-xs sm:text-sm leading-tight truncate">Formulir Buku Induk</div>
              <div className="text-[10px] text-slate-500 mt-0.5 font-mono truncate">NISN: {data.nisn}</div>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold border shrink-0 ${statusMeta.cls}`} data-testid="student-status-badge">
            <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
            <span className="hidden sm:inline">{statusMeta.label}</span>
            <span className="sm:hidden">{statusMeta.label.split(" ")[0]}</span>
          </span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {data.field_notes?.__global__ && data.status === "needs_fix" && (
          <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm" data-testid="global-correction-note">
            <div className="font-semibold mb-1 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
              Catatan Perbaikan dari Admin TU
            </div>
            <div className="text-rose-700">{data.field_notes.__global__}</div>
          </div>
        )}
        {data.status === "needs_fix" && Object.entries(data.documents || {}).some(([, d]) => d.status === "needs_fix" && d.note) && (
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="doc-corrections-note">
            <div className="font-semibold mb-2">Dokumen yang perlu diperbaiki:</div>
            <ul className="space-y-1 text-amber-800 text-xs">
              {Object.entries(data.documents || {}).filter(([, d]) => d.status === "needs_fix" && d.note).map(([type, d]) => (
                <li key={type}>• <strong>{DOC_META[type]?.label || type}</strong>: {d.note}</li>
              ))}
            </ul>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full bg-slate-100 h-auto p-1 rounded-xl gap-1" data-testid="portal-tabs">
            <TabsTrigger value="pribadi" className="py-2 text-xs sm:text-sm" data-testid="tab-pribadi">
              <span className="sm:hidden">Pribadi</span>
              <span className="hidden sm:inline">Data Pribadi</span>
            </TabsTrigger>
            <TabsTrigger value="alamat" className="py-2 text-xs sm:text-sm" data-testid="tab-alamat">
              <span className="sm:hidden">Alamat</span>
              <span className="hidden sm:inline">Alamat & Sekolah</span>
            </TabsTrigger>
            <TabsTrigger value="ortu" className="py-2 text-xs sm:text-sm" data-testid="tab-ortu">
              <span className="sm:hidden">Ortu/Wali</span>
              <span className="hidden sm:inline">Orang Tua/Wali</span>
            </TabsTrigger>
            <TabsTrigger value="dokumen" className="py-2 text-xs sm:text-sm" data-testid="tab-dokumen">Dokumen</TabsTrigger>
          </TabsList>

          <TabsContent value="pribadi" className="mt-6 space-y-6">
            <Card className="border-blue-200 bg-blue-50/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-display flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-700" />
                  Kelas Saat Ini (Tahun Ajaran Berjalan)
                </CardTitle>
                <CardDescription>
                  Kelas ini akan diperbarui setiap awal tahun ajaran. Contoh: dari 7A ke 8D setelah kenaikan kelas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-xs">
                  <Label className="text-slate-700">Kelas</Label>
                  <Select value={data.kelas || ""} onValueChange={(v) => setField("kelas", v)}>
                    <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="kelas-select">
                      <SelectValue placeholder="Pilih Kelas..." />
                    </SelectTrigger>
                    <SelectContent>
                      {KELAS_LIST.map((k) => (
                        <SelectItem key={k} value={k}>Kelas {k}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg font-display">A. Identitas Peserta Didik</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <Field label="Nama Lengkap *" value={data.nama_lengkap} onChange={(v) => setField("nama_lengkap", v)} testid="nama-lengkap-input" />
                <Field label="Nama Panggilan" value={data.nama_panggilan} onChange={(v) => setField("nama_panggilan", v)} testid="nama-panggilan-input" />
                <Field label="NIS" value={data.nis} onChange={(v) => setField("nis", v)} testid="nis-input" />
                <Field label="NIK" value={data.nik} onChange={(v) => setField("nik", v)} testid="nik-input" />
                <div>
                  <Label className="text-slate-700">Jenis Kelamin</Label>
                  <Select value={data.jenis_kelamin} onValueChange={(v) => setField("jenis_kelamin", v)}>
                    <SelectTrigger className="mt-1.5 h-10" data-testid="jenis-kelamin-select"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Laki-laki">Laki-laki</SelectItem>
                      <SelectItem value="Perempuan">Perempuan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Agama</Label>
                  <Select value={data.agama} onValueChange={(v) => setField("agama", v)}>
                    <SelectTrigger className="mt-1.5 h-10" data-testid="agama-select"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      {["Islam", "Kristen", "Katolik", "Hindu", "Buddha", "Konghucu", "Kepercayaan"].map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Tempat Lahir" value={data.tempat_lahir} onChange={(v) => setField("tempat_lahir", v)} testid="tempat-lahir-input" />
                <Field label="Tanggal Lahir" type="date" value={data.tanggal_lahir} onChange={(v) => setField("tanggal_lahir", v)} testid="tanggal-lahir-input" />
                <Field label="Anak ke-" value={data.anak_ke} onChange={(v) => setField("anak_ke", v)} testid="anak-ke-input" />
                <Field label="Jumlah Saudara Kandung" value={data.jumlah_saudara} onChange={(v) => setField("jumlah_saudara", v)} testid="jumlah-saudara-input" />
                <Field label="Jumlah Saudara Tiri" value={data.jumlah_saudara_tiri} onChange={(v) => setField("jumlah_saudara_tiri", v)} testid="jumlah-saudara-tiri-input" />
                <Field label="Jumlah Saudara Angkat" value={data.jumlah_saudara_angkat} onChange={(v) => setField("jumlah_saudara_angkat", v)} testid="jumlah-saudara-angkat-input" />
                <div>
                  <Label className="text-slate-700">Anak Yatim / Yatim Piatu</Label>
                  <Select value={data.anak_yatim || "Tidak"} onValueChange={(v) => setField("anak_yatim", v)}>
                    <SelectTrigger className="mt-1.5 h-10" data-testid="anak-yatim-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tidak">Tidak</SelectItem>
                      <SelectItem value="Yatim">Yatim</SelectItem>
                      <SelectItem value="Piatu">Piatu</SelectItem>
                      <SelectItem value="Yatim Piatu">Yatim Piatu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Golongan Darah" value={data.golongan_darah} onChange={(v) => setField("golongan_darah", v)} testid="gol-darah-input" />
                <Field label="Bahasa Sehari-hari" value={data.bahasa} onChange={(v) => setField("bahasa", v)} testid="bahasa-input" />
                <Field label="Tinggi Badan (cm)" value={data.tinggi_badan} onChange={(v) => setField("tinggi_badan", v)} testid="tinggi-badan-input" />
                <Field label="Berat Badan (kg)" value={data.berat_badan} onChange={(v) => setField("berat_badan", v)} testid="berat-badan-input" />
                <Field label="Penyakit Pernah Diderita" value={data.penyakit} onChange={(v) => setField("penyakit", v)} testid="penyakit-input" />
                <Field label="Kelainan Jasmani" value={data.kelainan_jasmani} onChange={(v) => setField("kelainan_jasmani", v)} testid="kelainan-jasmani-input" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alamat" className="mt-6 space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-lg font-display">B. Alamat Tempat Tinggal</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-slate-700">Alamat</Label>
                  <Textarea rows={2} value={data.alamat} onChange={(e) => setField("alamat", e.target.value)} className="mt-1.5" data-testid="alamat-input" />
                </div>
                <Field label="Kelurahan/Desa" value={data.kelurahan} onChange={(v) => setField("kelurahan", v)} testid="kelurahan-input" />
                <Field label="Kecamatan" value={data.kecamatan} onChange={(v) => setField("kecamatan", v)} testid="kecamatan-input" />
                <Field label="Kabupaten/Kota" value={data.kabupaten} onChange={(v) => setField("kabupaten", v)} testid="kabupaten-input" />
                <Field label="Provinsi" value={data.provinsi} onChange={(v) => setField("provinsi", v)} testid="provinsi-input" />
                <Field label="Kode Pos" value={data.kode_pos} onChange={(v) => setField("kode_pos", v)} testid="kode-pos-input" />
                <Field label="Nomor HP" value={data.nomor_hp} onChange={(v) => setField("nomor_hp", v)} testid="nomor-hp-input" />
                <Field label="Tinggal Bersama" value={data.tinggal_bersama} onChange={(v) => setField("tinggal_bersama", v)} testid="tinggal-bersama-input" />
                <Field label="Jarak ke Sekolah (km)" value={data.jarak_sekolah} onChange={(v) => setField("jarak_sekolah", v)} testid="jarak-sekolah-input" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg font-display">C. Pendidikan Sebelumnya</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <Field label="Asal Sekolah (SD/MI)" value={data.asal_sekolah} onChange={(v) => setField("asal_sekolah", v)} testid="asal-sekolah-input" />
                <Field label="NPSN Asal" value={data.npsn_asal} onChange={(v) => setField("npsn_asal", v)} testid="npsn-asal-input" />
                <Field label="Nomor STTB / Ijazah" value={data.nomor_sttb || data.nomor_ijazah} onChange={(v) => setField("nomor_sttb", v)} testid="nomor-ijazah-input" />
                <Field label="Tahun Ijazah" value={data.tahun_ijazah} onChange={(v) => setField("tahun_ijazah", v)} testid="tahun-ijazah-input" />
                <Field label="Lama Belajar" value={data.lama_belajar} onChange={(v) => setField("lama_belajar", v)} testid="lama-belajar-input" />
                <Field label="Tanggal Diterima" type="date" value={data.tanggal_diterima} onChange={(v) => setField("tanggal_diterima", v)} testid="tanggal-diterima-input" />
                <Field label="Pindahan Dari Sekolah" value={data.pindahan_dari} onChange={(v) => setField("pindahan_dari", v)} testid="pindahan-dari-input" />
                <div className="md:col-span-2">
                  <Label className="text-slate-700">Alasan Pindah (jika pindahan)</Label>
                  <Textarea rows={2} value={data.pindahan_alasan || ""} onChange={(e) => setField("pindahan_alasan", e.target.value)} className="mt-1.5" data-testid="pindahan-alasan-input" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg font-display">D. Kegemaran Siswa</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <Field label="Kesenian" value={data.kegemaran_kesenian} onChange={(v) => setField("kegemaran_kesenian", v)} testid="kegemaran-kesenian-input" />
                <Field label="Olahraga" value={data.kegemaran_olahraga} onChange={(v) => setField("kegemaran_olahraga", v)} testid="kegemaran-olahraga-input" />
                <Field label="Kemasyarakatan / Organisasi" value={data.kegemaran_organisasi} onChange={(v) => setField("kegemaran_organisasi", v)} testid="kegemaran-org-input" />
                <Field label="Lain-lain" value={data.kegemaran_lain} onChange={(v) => setField("kegemaran_lain", v)} testid="kegemaran-lain-input" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ortu" className="mt-6 space-y-6">
            <ParentSection title="D. Data Ayah Kandung" value={data.ayah || empty.ayah} onChange={(k, v) => setNested("ayah", k, v)} prefix="ayah" />
            <ParentSection title="E. Data Ibu Kandung" value={data.ibu || empty.ibu} onChange={(k, v) => setNested("ibu", k, v)} prefix="ibu" />
            <WaliSection value={data.wali || empty.wali} onChange={(k, v) => setNested("wali", k, v)} />
          </TabsContent>

          <TabsContent value="dokumen" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-display">F. Dokumen Pendukung</CardTitle>
                <CardDescription>Unggah dokumen berikut dalam format PDF/JPG/PNG (maks. 5MB per berkas)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(DOC_META).map(([type, meta]) => (
                  <DocumentRow key={type} type={type} label={meta.label} docs={data.documents || {}} onUpload={uploadDoc} />
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-6 px-6 py-4 flex items-center justify-between shadow-lg">
          <div className="text-sm text-slate-500">Data akan tersimpan dan bisa dilanjutkan kapan saja</div>
          <Button onClick={save} disabled={loading} className="bg-blue-700 hover:bg-blue-800 rounded-lg h-11 px-6" data-testid="save-biodata-button">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Simpan Biodata
          </Button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <Label className="text-slate-700">{label}</Label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10" data-testid={testid} />
  </div>
);

const ParentSection = ({ title, value, onChange, prefix }) => (
  <Card>
    <CardHeader><CardTitle className="text-lg font-display">{title}</CardTitle></CardHeader>
    <CardContent className="grid md:grid-cols-2 gap-4">
      <Field label="Nama" value={value.nama} onChange={(v) => onChange("nama", v)} testid={`${prefix}-nama-input`} />
      <Field label="NIK" value={value.nik} onChange={(v) => onChange("nik", v)} testid={`${prefix}-nik-input`} />
      <Field label="Tempat Lahir" value={value.tempat_lahir} onChange={(v) => onChange("tempat_lahir", v)} testid={`${prefix}-tempat-lahir-input`} />
      <Field label="Tanggal Lahir" type="date" value={value.tanggal_lahir} onChange={(v) => onChange("tanggal_lahir", v)} testid={`${prefix}-tanggal-lahir-input`} />
      <Field label="Agama" value={value.agama} onChange={(v) => onChange("agama", v)} testid={`${prefix}-agama-input`} />
      <Field label="Pendidikan Terakhir" value={value.pendidikan} onChange={(v) => onChange("pendidikan", v)} testid={`${prefix}-pendidikan-input`} />
      <Field label="Pekerjaan" value={value.pekerjaan} onChange={(v) => onChange("pekerjaan", v)} testid={`${prefix}-pekerjaan-input`} />
      <Field label="Penghasilan/Bulan" value={value.penghasilan} onChange={(v) => onChange("penghasilan", v)} testid={`${prefix}-penghasilan-input`} />
      <Field label="Nomor HP" value={value.nomor_hp} onChange={(v) => onChange("nomor_hp", v)} testid={`${prefix}-nomor-hp-input`} />
      <div className="md:col-span-2">
        <Label className="text-slate-700">Alamat</Label>
        <Textarea rows={2} value={value.alamat || ""} onChange={(e) => onChange("alamat", e.target.value)} className="mt-1.5" data-testid={`${prefix}-alamat-input`} />
      </div>
    </CardContent>
  </Card>
);

const WaliSection = ({ value, onChange }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg font-display">F. Data Wali (Opsional)</CardTitle>
      <CardDescription>Isi jika siswa tinggal dengan wali selain orang tua kandung</CardDescription>
    </CardHeader>
    <CardContent className="grid md:grid-cols-2 gap-4">
      <Field label="Nama Wali" value={value.nama} onChange={(v) => onChange("nama", v)} testid="wali-nama-input" />
      <Field label="NIK Wali" value={value.nik} onChange={(v) => onChange("nik", v)} testid="wali-nik-input" />
      <Field label="Hubungan dengan Siswa" value={value.hubungan} onChange={(v) => onChange("hubungan", v)} testid="wali-hubungan-input" />
      <Field label="Pekerjaan" value={value.pekerjaan} onChange={(v) => onChange("pekerjaan", v)} testid="wali-pekerjaan-input" />
      <Field label="Penghasilan/Bulan" value={value.penghasilan} onChange={(v) => onChange("penghasilan", v)} testid="wali-penghasilan-input" />
      <Field label="Nomor HP" value={value.nomor_hp} onChange={(v) => onChange("nomor_hp", v)} testid="wali-nomor-hp-input" />
      <div className="md:col-span-2">
        <Label className="text-slate-700">Alamat</Label>
        <Textarea rows={2} value={value.alamat || ""} onChange={(e) => onChange("alamat", e.target.value)} className="mt-1.5" data-testid="wali-alamat-input" />
      </div>
    </CardContent>
  </Card>
);

const DocumentRow = ({ type, label, docs, onUpload }) => {
  const doc = docs[type];
  const status = doc?.status || "draft";
  const meta = STATUS_META[status] || STATUS_META.draft;
  const inputId = `upload-${type}`;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-slate-200 rounded-lg bg-white hover:bg-slate-50/50 transition-colors" data-testid={`doc-row-${type}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-slate-900">{label}</span>
          {doc && (
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`} data-testid={`doc-status-${type}`}>
              <span className={`w-1 h-1 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
          )}
        </div>
        {doc && <div className="text-xs text-slate-500 mt-1 truncate">{doc.filename} • {(doc.size / 1024).toFixed(1)} KB</div>}
        {doc?.note && status === "needs_fix" && <div className="text-xs text-rose-700 mt-1">Catatan: {doc.note}</div>}
      </div>
      <div className="flex items-center gap-2">
        {doc && (
          <a href={`${fileUrl(doc.path)}`} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline font-medium" data-testid={`view-doc-${type}`}>
            Lihat
          </a>
        )}
        <label htmlFor={inputId} className="cursor-pointer">
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold transition-colors" data-testid={`upload-btn-${type}`}>
            <Upload className="w-3.5 h-3.5" />
            {doc ? "Ganti" : "Unggah"}
          </span>
        </label>
        <input id={inputId} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(type, f); e.target.value = ""; }} data-testid={`upload-input-${type}`} />
      </div>
    </div>
  );
};
